import { useMemo } from "react";

import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

import type { DistributionData } from "../types";

type Props = {
  data: DistributionData[];
  hoverIndex: number | null;
  setHoverIndex: (value: number | null) => void;
};

export const WasteStreamDistributionGraph: React.FC<Props> = ({
  data,
  hoverIndex,
  setHoverIndex,
}) => {
  const total = useMemo(
    () => data.reduce((sum, s) => sum + s.value, 0),
    [data],
  );

  const hovered = useMemo(
    () => (hoverIndex != null ? data[hoverIndex] : null),
    [hoverIndex, data],
  );

  const hoveredPct = useMemo(
    () => (hovered && total ? Math.round((hovered.value / total) * 100) : 0),
    [hovered, total],
  );

  return (
    <div className="relative h-[200px] w-[200px] flex-none">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="label"
            innerRadius="70%"
            outerRadius="100%"
            paddingAngle={1}
            stroke="none"
            isAnimationActive={false}
            onMouseEnter={(_, index) => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          >
            {data.map((s, i) => (
              <Cell
                key={s.label}
                fill={s.color}
                opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.3}
                style={{ cursor: "pointer", transition: "opacity .12s" }}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-signifier text-[22px] leading-none">
          {hovered ? `${hoveredPct} %` : total.toLocaleString()}
        </span>
        <span className="text-grey6 mt-[4.95px] max-w-[90.75px] truncate text-[10.5px]">
          {hovered ? hovered.label : "kg total"}
        </span>
      </div>
    </div>
  );
};
